import React from 'react';
import { useTranslation } from '@shared/core';
import { AddCircle, MapMarker } from '@withjoy/joykit/icons';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { TravelMapBlockType } from '../../Accommodations.types';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import type { EventVenueState } from '../EventVenueDialog/EventVenueDialog.types';
import { useEventVenueDialog } from '../EventVenueDialog/useEventVenueDialog';
import type { EventVenue } from '@shared/utils/eventPlace';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { TextV2 } from '@shared/joykit/packages/core/components/TextV2';

type VenueInfoV2Props = Readonly<{
  onShowFeedback?: () => void;
  venueInfoProps: UseVenueInfoReturn;
}>;

export type UseVenueInfoReturn = Readonly<{
  handleAddVenue: () => void;
  handleEditVenue: () => void;
  venue: EventVenue | undefined;
  venueInfo: EventVenue | undefined;
  eventVenueState: EventVenueState;
  isSuperAdmin?: boolean;
  pageSlug: string | undefined;
  isOpen: () => boolean;
  onClose: () => void;
}>;

export const useVenueInfo = (): UseVenueInfoReturn => {
  const { venueData: venue, pageSlug, isSuperAdmin } = useAccommodationsContext();

  const websiteEditorTelemetry = useAdminAccommodationsTelemetry();
  const { setCustomComponentBlockData } = useCustomComponentStateContext<EventVenue>();

  const { venueInfo, eventVenueState, setEventVenueState, isOpen, onClose, requestOpen } = useEventVenueDialog();

  const handleAddVenue = useEventCallback(() => {
    websiteEditorTelemetry.addItemClicked(pageSlug, { itemType: TravelMapBlockType.travelMapVenue });
    setEventVenueState('create');
    requestOpen();
  });

  const handleEditVenue = useEventCallback(() => {
    websiteEditorTelemetry.editItemClicked(pageSlug, { itemType: TravelMapBlockType.travelMapVenue });
    if (venue) {
      setCustomComponentBlockData(venue);
    }
    // D. Have venue. Navigate to "Where to Stay" --> Click on "Your Venue" -> "Update Venue"
    setEventVenueState('update');
    requestOpen();
  });

  return {
    handleAddVenue,
    handleEditVenue,
    venue,
    venueInfo,
    eventVenueState,
    isSuperAdmin,
    pageSlug,
    isOpen,
    onClose,
  };
};

const VenueDetailsCompact = ({ venueName, handleEditVenue }: { venueName: string; handleEditVenue: () => void }) => {
  const isMobile = useIsMobileScreen();
  return (
    <Flex
      justifyItems="center"
      color="mono14"
      _hover={{ color: 'newUITest1' }}
      alignItems="center"
      gap={2}
      paddingX={4}
      marginBottom={0}
      onClick={handleEditVenue}
      cursor="pointer"
      height="auto"
      width="100%"
      overflow="hidden"
      minWidth={0}
    >
      {!isMobile && (
        <MapMarker
          size={16}
          flexShrink={0}
        />
      )}
      <TextV2
        typographyVariant={'label2'}
        flex={1}
        minWidth={0}
        whiteSpace="nowrap"
        textOverflow="ellipsis"
        overflow="hidden"
      >
        {venueName}
      </TextV2>
    </Flex>
  );
};

const AddVenueButtonCompact = ({ handleAddVenue, addVenueLabel }: { handleAddVenue: () => void; addVenueLabel: string }) => {
  return (
    <Flex
      width="100%"
      alignItems="center"
      justifyContent="center"
      marginBottom={0}
      paddingY={1}
    >
      <ButtonV2
        intent="neutral"
        variant="outline"
        shape="rounded"
        startIcon={<AddCircle size={16} />}
        onClick={handleAddVenue}
        height={7}
        width={pxToRem(140)}
        typographyVariant={'label2'}
        border="none"
        paddingY={4}
        _hover={{ backgroundColor: 'transparent' }}
        _active={{ backgroundColor: 'transparent' }}
      >
        {addVenueLabel}
      </ButtonV2>
    </Flex>
  );
};

export const VenueInfoCompact = ({ venueInfoProps }: VenueInfoV2Props) => {
  const { venue, handleEditVenue, handleAddVenue } = venueInfoProps;
  const { tq } = useTranslation('travelMap');

  return (
    <Flex width="100%">
      {venue?.hasVenue ? (
        <Flex
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <VenueDetailsCompact
            venueName={venue?.name || ''}
            handleEditVenue={handleEditVenue}
          />
        </Flex>
      ) : (
        <AddVenueButtonCompact
          handleAddVenue={handleAddVenue}
          addVenueLabel={tq`Add Venue`}
        />
      )}
    </Flex>
  );
};
